import {
    StyleFunction,
    Surface,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQLayoutCreateMutation, useLayoutCreateMutation } from "graphql-sdk";
import {
    ILayoutCreate,
    layoutRestaurantLarge,
    layoutRestaurantMedium,
    layoutRestaurantSmall,
    LayoutSection,
    schemaLayout,
} from "lib";
import React, { useCallback } from "react";
import { useTranslation } from "locales";

import { useHandleMutationError } from "../../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../../navigation";
import { defaultLayout } from "../../forms";
import { LayoutCreateForm } from "../../layout-create-form";
import { LayoutScreen } from "../../layout-screen";

import type { FetchResult } from "@apollo/client";
import { produceNewLayoutSection } from "../../functions";

const tableLayoutSizes = [
    layoutRestaurantSmall,
    layoutRestaurantMedium,
    layoutRestaurantLarge,
];

type ScreenProps = RootStackScreenProps<"TABLE_LAYOUT_CREATE">;

export function TableLayoutCreateScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.layout.write");

    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();
    const [layoutCreate] = useLayoutCreateMutation();

    const form = useForm<ILayoutCreate>(schemaLayout, defaultLayout);
    const [{ values }, { reset }] = form;

    const create = useCallback(async () => {
        if (!values || !values.columns || !values.rows) {
            return;
        }

        const emptyLayoutSection: LayoutSection = produceNewLayoutSection(
            t,
            "1"
        );

        let createResult: FetchResult<
            GQLayoutCreateMutation,
            Record<string, any>,
            Record<string, any>
        >;
        await handleMutationError(
            async () => {
                createResult = await layoutCreate({
                    variables: {
                        layout: {
                            name: values.name,
                            data: JSON.stringify([emptyLayoutSection]),
                            columns: values.columns,
                            rows: values.rows,
                            layoutType: "TABLE",
                        },
                    },
                });
            },
            t("common.created", "Created"),
            () => {
                if (!createResult.data || !createResult.data?.layoutCreate.id) {
                    return;
                }

                reset();

                if (!route.params || !route.params.referrer) {
                    navigate("TABLE_LAYOUT_EDIT", {
                        id: createResult.data.layoutCreate.id,
                    });
                    return;
                }

                navigate("TABLE_LAYOUT_EDIT", {
                    id: createResult.data.layoutCreate.id,
                    referrer: {
                        route: route.params.referrer.route,
                        id: route.params.referrer.id,
                    },
                });
            }
        );
    }, [
        values,
        t,
        handleMutationError,
        layoutCreate,
        route.params,
        reset,
        navigate,
    ]);

    return (
        <LayoutScreen>
            <Surface style={styles.container}>
                <LayoutCreateForm
                    form={form}
                    onSubmit={create}
                    submitButton={["common.create", "Create"]}
                    layoutSizes={tableLayoutSizes}
                />
            </Surface>
        </LayoutScreen>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: { maxWidth: theme.dimensions.maxFormWidth },
});
